/**
 * Simple hook for managing recent search history in localStorage.
 * Stores last 5 searches, deduped by entity_id.
 */

import { useState } from 'react';

import { SearchResult } from '@/services/api/searchService';

const STORAGE_KEY = 'finch_recent_searches';
const MAX_RECENT = 5;

const getRecent = (): SearchResult[] => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return [];
  const parsed = JSON.parse(stored);
  return Array.isArray(parsed) ? parsed : [];
};

export const useRecentSearches = () => {
  const [recentSearches, setRecentSearches] =
    useState<SearchResult[]>(getRecent());

  const saveRecentSearch = (result: SearchResult) => {
    // Dedupe by entity (for coverage, also include claim_identifier to allow multiple claims)
    const filtered = recentSearches.filter((r) => {
      // Basic deduplication by entity type and ID
      if (
        r.entity_type !== result.entity_type ||
        r.entity_id !== result.entity_id
      ) {
        return true; // Different entity, keep it
      }

      // For coverage results, also check claim_identifier to allow multiple claims from same coverage
      if (result.entity_type === 'coverage') {
        const rClaimId =
          'claim_identifier' in r.metadata ? r.metadata.claim_identifier : null;
        const resultClaimId =
          'claim_identifier' in result.metadata
            ? result.metadata.claim_identifier
            : null;

        // Keep if claim identifiers are different
        return rClaimId !== resultClaimId;
      }

      // Same entity type and ID, filter it out (replace with new one)
      return false;
    });

    // Add to front, keep max 5
    const updated = [result, ...filtered].slice(0, MAX_RECENT);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    setRecentSearches(updated);
  };

  const clearRecentSearches = () => {
    localStorage.removeItem(STORAGE_KEY);
    setRecentSearches([]);
  };

  return { recentSearches, saveRecentSearch, clearRecentSearches };
};
